import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import { ReferralStage, ReferralSource, ReferralStatus, FlagSeverity } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import SourceBadge from '../common/SourceBadge';
import ReferralStageBadge from '../common/ReferralStageBadge';

type SortField = 'patient' | 'source' | 'stage' | 'status' | 'receivedAt' | 'updatedAt';
type SortDir = 'asc' | 'desc';

const STAGE_ORDER: Record<ReferralStage, number> = {
  [ReferralStage.INTAKE]: 0,
  [ReferralStage.PROCESSING]: 1,
  [ReferralStage.DECISION]: 2,
  [ReferralStage.EXECUTION]: 3,
};

const STATUS_LABELS: Record<string, string> = {
  RECEIVED: 'Received',
  PROCESSING: 'Processing',
  PROCESSING_COMPLETE: 'Processing complete',
  AWAITING_INFO: 'Awaiting info',
  RECOMMENDATION_READY: 'Ready for decision',
  PENDING_REVIEW: 'Pending review',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  BLOCKED: 'Blocked',
};

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

interface ReferralListTableProps {
  filterSource: ReferralSource | 'ALL';
  filterStage: ReferralStage | 'ALL';
}

const ReferralListTable: React.FC<ReferralListTableProps> = ({ filterSource, filterStage }) => {
  const { referrals } = useTransitionContext();
  const navigate = useNavigate();

  const [sortField, setSortField] = useState<SortField>('receivedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let rows = referrals.filter((r) => r.status !== ReferralStatus.DECLINED && r.status !== ReferralStatus.AUTO_DECLINED);
    if (filterSource !== 'ALL') rows = rows.filter((r) => r.source === filterSource);
    if (filterStage !== 'ALL') rows = rows.filter((r) => r.stage === filterStage);
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'patient':
          cmp = a.patient.name.localeCompare(b.patient.name);
          break;
        case 'source':
          cmp = a.source.localeCompare(b.source);
          break;
        case 'stage':
          cmp = STAGE_ORDER[a.stage] - STAGE_ORDER[b.stage];
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'receivedAt':
          cmp = new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime();
          break;
        case 'updatedAt':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [referrals, filterSource, filterStage, sortField, sortDir]);

  const sortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDir : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('patient', 'Patient')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Patient Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('source', 'Source')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('stage', 'Stage')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('status', 'Status')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('receivedAt', 'Received')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{sortLabel('updatedAt', 'Last Updated')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.disabled">
                    No referrals match the current filters
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((referral) => {
                const blocking = referral.flags.filter((f) => f.severity === FlagSeverity.BLOCKING).length;
                const warning = referral.flags.filter((f) => f.severity === FlagSeverity.WARNING).length;

                return (
                  <TableRow
                    key={referral.id}
                    hover
                    onClick={() => navigate(`/transition/referral/${referral.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {referral.patient.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {referral.patient.requestedProgram}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(referral.priorEncounters?.length ?? 0) > 0 ? (
                        <Chip
                          icon={<HistoryIcon sx={{ fontSize: '0.7rem !important' }} />}
                          label="Returning"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                        />
                      ) : (
                        <Chip
                          label="New"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 24, color: 'text.disabled', borderColor: 'divider' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <SourceBadge source={referral.source} size="small" />
                    </TableCell>
                    <TableCell>
                      <ReferralStageBadge stage={referral.stage} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {STATUS_LABELS[referral.status] ?? referral.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {blocking > 0 && (
                          <Chip
                            label={`${blocking} blocker`}
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                        {warning > 0 && (
                          <Chip
                            label={`${warning} warning`}
                            size="small"
                            color="warning"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                        {blocking === 0 && warning === 0 && (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(referral.receivedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(referral.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Open referral">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/transition/referral/${referral.id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReferralListTable;
